import React, {useEffect, useState} from 'react'
import {Box, Button, Drawer, IconButton, Stack, Tooltip, Typography} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import moment from 'moment'
import {useAppContext} from '@context/appContext/hooks/useAppContext'
import {useGetCameraSnapsByTimeQuery} from '@containers/cameras/queryHooks/useCamerasQuery'
import {DATE_FORMAT_CAMERAS} from '@containers/cameras/constants/dateFormat'
import SpinnerCustom from '@ui/spinner/SpinnerCustom'
import {ISegment} from '@containers/monitoringPlatform/passengerVerification/helpers/buildSegments'
import {formatRange} from '@containers/monitoringPlatform/passengerCharts/helpers/formatTime'
import SegmentPhoto from '@containers/monitoringPlatform/passengerVerification/photos/SegmentPhoto'
import PhotoViewer from '@containers/monitoringPlatform/passengerVerification/photos/PhotoViewer'
import sx from '@containers/monitoringPlatform/passengerVerification/photos/sx'

const MARGIN_SECONDS = 60

interface IProps {
	segment: ISegment | null
	onClose: () => void
}

const SegmentPhotosPanel = ({segment, onClose}: IProps) => {
	const {account} = useAppContext()
	const [viewerIndex, setViewerIndex] = useState<number | null>(null)

	useEffect(() => {
		setViewerIndex(null)
	}, [segment?.key])
	const from = segment ? segment.from - MARGIN_SECONDS : 0
	const to = segment ? segment.to + MARGIN_SECONDS : 0

	const {data, isFetching} = useGetCameraSnapsByTimeQuery({
		accountId: account.id,
		deviceId: segment?.deviceId ?? undefined,
		dateFrom: moment(from * 1000).startOf('minute').format(DATE_FORMAT_CAMERAS),
		dateTo: moment(to * 1000).add(1, 'minute').startOf('minute').format(DATE_FORMAT_CAMERAS),
		enabled: !!segment?.deviceId,
	})

	const snaps = (data ?? [])
		.filter((s) => s.snapTime >= from && s.snapTime <= to)
		.filter((s) => !segment?.cameraIds.length || segment.cameraIds.includes(s.cameraId))
		.sort((a, b) => a.snapTime - b.snapTime)

	return (
		<Drawer anchor="right" open={!!segment} onClose={onClose} PaperProps={{sx: sx.paper}}>
			{segment && <Stack sx={sx.root}>
				<Box sx={sx.header}>
					<Box sx={{minWidth: 0}}>
						<Typography sx={sx.title}>{segment.fromStop} → {segment.toStop}</Typography>
						<Typography sx={sx.subtitle}>
							{formatRange(segment.from, segment.to)} · {segment.licencePlate ?? '—'} · Socium {segment.avg ?? '—'}
						</Typography>
					</Box>
					<IconButton size="small" onClick={onClose} aria-label="close">
						<CloseIcon fontSize="small"/>
					</IconButton>
				</Box>

				<Tooltip title="Незабаром">
					<span>
						<Button variant="contained" fullWidth disabled sx={sx.incident}>Інцидент</Button>
					</span>
				</Tooltip>

				{isFetching && <SpinnerCustom/>}

				{!isFetching && !snaps.length && <Typography sx={sx.subtitle}>
					Немає фото за цей час
				</Typography>}

				<Stack gap={1.5}>
					{snaps.map((snap, index) => (
						<Box key={`${snap.cameraId}-${snap.snapTime}`} sx={sx.photo}>
							<SegmentPhoto snap={snap} sx={sx.image} onClick={() => setViewerIndex(index)}/>
							<Box sx={sx.caption}>
								<span>{moment(snap.snapTime * 1000).format('HH:mm:ss')} · кам. {snap.cameraNumber}</span>
								{snap.passengerCount != null && <b>{snap.passengerCount} пас.</b>}
							</Box>
						</Box>
					))}
				</Stack>
			</Stack>}
			<PhotoViewer snaps={snaps} index={viewerIndex} onChange={setViewerIndex}/>
		</Drawer>
	)
}

export default SegmentPhotosPanel
